import * as yup from "yup"
import firebase from "../../firebase"
import draftToHtml from "draftjs-to-html"
import { Quill } from "react-quill"
import { v7 as uuid } from "uuid"

import _ from "lodash"

import {
  Answer,
  AnswerState,
  AssetRef,
  BlockRef,
  BlockState,
  CustomTheme,
  Duration,
  ElementState,
  ExerciseState,
  Feedback,
  FeedbackState,
  Hint,
  MetaType,
  Question,
  QuestionTarget,
  ScreenState,
  Settings,
  SeqRef,
  SnapPath,
  SnapRef,
  SnapShotRef,
  Other,
  Jump,
  InteractionType,
  Preferences,
  VideoSettings,
  TTS
} from "../types"

import {
  Asset,
  BlockEmpty,
  BlockImage,
  BlockInteraction,
  BlockPresentation,
  BlockPdf,
  BlockText,
  BlockVideo,
  ElementCourseState,
  Media,
  SeqAssessment,
  SeqConfig,
  SeqExercise,
  SeqLogic,
  SeqScreen,
  Snap,
  SnapCourse,
  SnapShot,
  SnapState,
  User,
  WordBank,
  BlockQtiInteraction,
  SeqQtiScreen,
  SeqPassage
} from "../collections"

import { MDtoHTML } from "../../components/EscapeText/EscapeText"
import { Explanation } from "../types/Explanation"
import { SnapConfig } from "../types"
import { Program } from "../../../../types/gqlTypes/graphql"
import { getSchoolYear } from "../../../mp-utils/generic-utils"
import {
  InlineInteractions,
  ResponseDeclaration
} from "../../authoring/pages/QtiAuthoring/helpers/types"
import { InlineContentType } from "../../authoring/pages/QtiAuthoring/customWidgets/InlineInteractionContainer/types"

export const schemaOptions = {
  strict: true,
  abortEarly: false,
  stripUnknown: true,
  recursive: true
}

// Schema Helpers
const EmptyStringSchema = yup.string().defined().default("")
const EmptyNullableStringSchema = yup.string().defined().nullable().default("")
const StringSchema = yup.string().defined()
const CompactArray = yup.array().compact(v => v == null)

export const AssetTypeSchema = yup
  .string()
  .oneOf([
    "image",
    "mux",
    "thumbnail",
    "blob",
    "word-bank",
    "pdf",
    "content-resource",
    "mux_subtitles",
    "mux_audio",
    "audio_description"
  ])
  .defined()

export const ShapeTypeSchema = yup
  .string()
  .oneOf(["circle", "default", "ellipse", "poly", "rect"])
  .defined()

export const BlockTypeSchema = yup
  .string()
  .oneOf([
    "presentation",
    "qtiInteraction",
    "interaction",
    "video",
    "image",
    "pdf",
    "text",
    "empty"
  ])
  .defined()

export const HintTypeSchema = yup
  .string()
  .oneOf(["video", "text", "video+text"])
  .defined()

export const AnswerTypeSchema = yup
  .string()
  .oneOf([
    "non-zone",
    "rate",
    "word-bank",
    "text",
    "rich-text",
    "math",
    "image",
    "video",
    "text-zone",
    "image-zone"
  ])
  .defined()

export const FeedbackTypeSchema = yup
  .string()
  .oneOf(["text", "text+video", "video"])
  .defined()

export const InteractionTypeSchema = yup
  .string()
  .oneOf([
    "mini-test",
    "multiple-choice",
    "poll",
    "clickzone",
    "assessment",
    "rate",
    "sort",
    "matching",
    "constructed-response",
    "multiple-select",
    "multiple-clickzone",
    "sequence",
    "word-cloud"
  ])
  .defined()

export const QtiInteractionTypeSchema = yup
  .string()
  .oneOf(["choice", "match", "hottext", "gapmatch", "hotspot", "extended-text"])
  .defined()

export const PresentationTypeSchema = yup
  .string()
  .oneOf(["fade", "slide", "none"])
  .defined()
  .default("fade")

const MediaTypeSchema = yup
  .string()
  .oneOf(["ppt", "video", "image", "pdf"])
  .defined()

const SnapTypeSchema = yup
  .string()
  .oneOf(["snap", "element", "assessment"])
  .defined()

const TextTypeSchema = yup
  .string()
  .oneOf(["passage", "citation", "header", "table", "opentext", "instruction"])
  .defined()
// export type LayoutType = 'Large Left' | 'Large Right' | 'Balanced' | 'Centered Column'//

const LayoutTypeSchema = yup
  .string()
  .oneOf(["Large Left", "Large Right", "Centered Column", "Balanced"])
  .defined()
const MobileLayoutTypeSchema = yup
  .string()
  .default("Stacked")
  .oneOf(["Stacked", "Tabbed"])

const SeqTypeSchema = yup
  .string()
  .oneOf([
    "config",
    "logic",
    "screen",
    "exercise",
    "assessment",
    "qtiScreen",
    "passage"
  ])
  .defined()

const JumpTypeSchema = yup.string().oneOf(["branch", "snap", ""]).defined()

const TargetTypeSchema = yup
  .string()
  .nullable()
  .oneOf(["text", "image", null])
  .nullable()
  .defined()
const TargetSubTypeSchema = yup
  .string()
  .nullable()
  .oneOf(["opentext", "passage", "image", null])
  .defined()

const SnapProgramTypeSchema: yup.StringSchema<Program | "STAARv2"> = yup
  .string<Program | "STAARv2">()
  .nullable()
  .defined()
  .default(null)

const SnapSubjectTypeSchema: yup.StringSchema<string> = yup
  .string()
  .nullable()
  .defined()
  .default(null)

const CustomThemeSchema: yup.ObjectSchema<CustomTheme> = yup
  .object({
    dark: EmptyStringSchema,
    primaryColor: EmptyStringSchema,
    secondaryColor: EmptyStringSchema
  })
  .defined()

const VideoSettingsSchema: yup.ObjectSchema<VideoSettings> = yup
  .object({
    playerVolume: yup.number().required().default(0)
  })
  .defined()

const PreferencesSchema = yup
  .object({
    videoSettings: VideoSettingsSchema
  })
  .defined() as yup.ObjectSchema<Preferences>

const FirestoreTimeStampSchema: yup.ObjectSchema<
  Pick<firebase.firestore.Timestamp, "seconds" | "nanoseconds">
> = yup
  .object({
    nanoseconds: yup
      .number()
      .positive()
      .defined()
      .default(() => firebase.firestore.Timestamp.now()["nanoseconds"]),
    seconds: yup
      .number()
      .positive()
      .defined()
      .default(() => firebase.firestore.Timestamp.now()["seconds"])
  })
  .defined()

export const MetaTypeSchema = yup
  .object({
    owner_id: StringSchema.default(() =>
      firebase.auth().currentUser
        ? firebase.auth().currentUser?.uid ?? ""
        : "system"
    ),
    last_updated_by: StringSchema.default(() =>
      firebase.auth().currentUser
        ? firebase.auth().currentUser?.uid ?? ""
        : "system"
    ),
    updated: FirestoreTimeStampSchema,
    created: FirestoreTimeStampSchema,
    deleted: yup.boolean().defined().default(false)
  })
  .defined() as yup.ObjectSchema<MetaType>

const JumpSchema = yup
  .object({
    type: JumpTypeSchema.defined().default(""),
    id: yup.string().defined().defined().default("")
  })
  .nullable()
  .defined()
  .transform(function (v: Jump, ov: Jump) {
    // Check if either is empty string or undefined
    if (!v?.type || !v?.id) {
      // Null it as we don't allow empty strings
      return null
    }

    return ov
  })

const CropBoxSchema = yup.object({
  left: yup.number().defined(),
  top: yup.number().defined(),
  width: yup.number().defined(),
  height: yup.number().defined(),
  naturalWidth: yup.number().defined(),
  naturalHeight: yup.number().defined(),
  aspectRatio: yup.number().defined(),
  rotate: yup.number().defined(),
  scaleX: yup.number().defined(),
  scaleY: yup.number().defined()
})

const CropSchema = yup
  .object({
    xs: CropBoxSchema,
    sm: CropBoxSchema,
    md: CropBoxSchema,
    lg: CropBoxSchema,
    xl: CropBoxSchema
  })
  .defined()

const TrimDataTypeScheme = yup
  .object({
    start: yup.number().nullable().defined(),
    end: yup.number().nullable().defined()
  })
  .defined()
  .nullable()

const CropDataTypeScheme = yup
  .object({
    x: yup.number().defined(),
    y: yup.number().defined(),
    width: yup.number().defined(),
    height: yup.number().defined(),
    rotate: yup.number().defined(),
    scaleX: yup.number().defined(),
    scaleY: yup.number().defined()
  })
  .defined()
  .nullable()

export type CropBox = yup.InferType<typeof CropBoxSchema>
export type CropType = yup.InferType<typeof CropSchema>

export const AssetRefSchema = yup
  .object({
    type: AssetTypeSchema.default("blob"),
    id: EmptyStringSchema,
    snap_id: EmptyStringSchema,
    altText: EmptyStringSchema,
    cropData: CropDataTypeScheme.default(null),
    trimData: TrimDataTypeScheme.default(null)
  })
  .defined() as yup.ObjectSchema<AssetRef>

export const DurationSchema: yup.ObjectSchema<Duration> = yup
  .object({
    minutes: yup.number().min(0).required().default(0),
    seconds: yup.number().min(0).required().default(0)
  })
  .defined()

export const TTSSchema = yup
  .object({
    asset: AssetRefSchema.defined(),
    wordDurations: yup
      .array()
      .of(
        yup.object({
          word: EmptyStringSchema,
          startMs: yup.number().defined(),
          endMs: yup.number().defined()
        })
      )
      .defined(),
    storagePath: EmptyStringSchema,
    md5: EmptyStringSchema
  })
  .defined() as yup.ObjectSchema<TTS>

export const FeedbackSchema = yup
  .object({
    type: FeedbackTypeSchema.default("text"),
    text: EmptyStringSchema,
    deltas: yup
      .mixed()
      .defined()
      .default({ ops: [{ insert: "\n" }] }),
    assets: CompactArray.of(AssetRefSchema).defined().ensure(),
    enable_replay: yup.boolean().defined().default(true),
    autoplay: yup.boolean().optional().default(true),
    showCaptions: yup.boolean().optional().default(false),
    videoTitle: EmptyStringSchema,
    answerIdentifier: yup.string().optional(),
    feedbackType: yup
      .string()
      .oneOf(["correct", "incorrect", "neutral"])
      .nullable()
      .optional()
      .default(null)
  })
  .defined() as yup.ObjectSchema<Feedback>

export const AnswerSchema = yup
  .object({
    id: yup.string().required(),
    jump: JumpSchema.nullable().default(null),
    answer: EmptyStringSchema,
    deltas: yup
      .mixed()
      .defined()
      .default({ ops: [{ insert: "\n" }] }),
    ocurrence: yup.number().min(-1).defined().default(-1),
    type: AnswerTypeSchema.default("text"),
    assets: CompactArray.of(AssetRefSchema).defined().ensure(),
    correct: yup.boolean().defined().default(false),
    feedbacks: CompactArray.of(FeedbackSchema).defined().ensure(),
    category: EmptyStringSchema.default("unnamed"),
    tts: TTSSchema.nullable().default(null)
  })
  .defined()
  .transform(function (v: Answer, ov: Answer) {
    let o

    if (v?.category === undefined || v?.category === null) {
      o = o ?? _.cloneDeep(v)
      o.category = "unnamed"
    }

    if (v?.id === undefined || v?.id === null || v?.id === "") {
      o = o ?? _.cloneDeep(v)
      o.id = uuid()
    }

    if (o) {
      return o
    }

    return ov
  })
  .test("category-null", "${path} was not converted.", v => {
    if (v?.category === undefined || v?.category === null) {
      return false
    }

    return true
  })
  .test("id-null", "${path} answer doesn't have id.", v => {
    if (v?.id === undefined || v?.id === null || v?.id === "") {
      return false
    }

    return true
  }) as yup.ObjectSchema<Answer>

export const ExplanationSchema: yup.ObjectSchema<Explanation> = yup
  .object({
    deltas: yup
      .mixed()
      .defined()
      .default({ ops: [{ insert: "\n" }] }),
    videoHTMLFrame: yup.string().defined(),
    videoThumbnailUrl: yup.string().defined()
  })
  .defined()

export const BlockRefSchema: yup.ObjectSchema<BlockRef> = yup
  .object({
    type: BlockTypeSchema.default("empty"),
    id: EmptyStringSchema
  })
  .defined()

export const QuestionTargetSchema: yup.ObjectSchema<QuestionTarget> = yup
  .object({
    type: TargetTypeSchema,
    id: EmptyStringSchema,
    subType: TargetSubTypeSchema
  })
  .defined()

export const HintSchema: yup.ObjectSchema<Hint> = yup
  .object({
    type: HintTypeSchema.default("text"),
    text: EmptyStringSchema,
    video_id: EmptyStringSchema
  })
  .defined()

export const SnapShotRefSchema: yup.ObjectSchema<SnapShotRef> = yup
  .object({
    id: EmptyStringSchema,
    version: EmptyStringSchema
  })
  .defined()

export const SeqRefSchema: yup.ObjectSchema<SeqRef> = yup
  .object({
    type: SeqTypeSchema.default("screen"),
    id: EmptyStringSchema
  })
  .defined()

export const SnapRefSchema = yup
  .object({
    id: EmptyStringSchema,
    versions: CompactArray.of(SnapShotRefSchema).defined().ensure(),
    thumbnail: EmptyStringSchema
  })
  .defined() as yup.ObjectSchema<SnapRef>

export const BlockTextSchema = yup
  .object({
    kind: yup.string().oneOf(["block_texts"]).defined().default("block_texts"),
    version: yup.number().min(2).defined().default(4),
    type: TextTypeSchema,
    text: EmptyStringSchema,
    meta: MetaTypeSchema.required(),
    html: EmptyStringSchema,
    draftjs: EmptyStringSchema,
    tts: TTSSchema.nullable().default(null),
    deltas: yup
      .mixed()
      .defined()
      .default({ ops: [{ insert: "\n" }] })
  })
  .transform(function (v, ov) {
    // WARNING: version 4 = new opentext text styling (ManagementTheme body1 / smaller).
    // Blocks with version < 4 render at legacy sizes (1rem/1.375rem desktop).
    // Do NOT add a v3→v4 migration transform — that would silently change font sizes
    // for all existing content. New blocks get version 4 via createTextRecord().
    if (v.version < 3) {
      const o = _.cloneDeep(v)

      if (v.version === 1 || (v.text !== "" && v.html === "")) {
        if (o.text !== "") {
          o.draftjs = _.clone(o.text)
          o.html = draftToHtml(JSON.parse(o.text))
          o.text = ""
        }

        o.version = 2
      }

      if (o.version === 2) {
        // loop through all the ops and find those with passageTable and convert to table
        if (Array.isArray(o.deltas?.ops)) {
          o.deltas.ops = o.deltas.ops.map(op => {
            if (typeof op?.insert?.passageTable === "object") {
              op.insert.table = {
                identifier: op.insert.passageTable.identifier ?? "",
                value: op.insert.passageTable.value ?? ""
              }
              delete op.insert.passageTable
            }

            return op
          })
        }

        o.version = 3
      }

      return o
    }

    return ov
  })
  .test("block_texts-old-version", "${path} was not converted.", v => {
    if (v.version < 3) {
      return false
    }

    return true
  })
  .defined() as yup.ObjectSchema<BlockText>

export const SettingsSchema = yup
  .object({
    disable_feedback: yup.boolean().defined().default(false),
    min_correct: yup.number().min(0).defined().default(0),
    max_retries: yup.number().min(0).defined().default(0),
    autosubmit: yup.boolean().defined().default(false),
    autocomplete: yup.boolean().defined().default(false),
    visible_blocks: CompactArray.of(QuestionTargetSchema),
    target: QuestionTargetSchema.nullable().defined().default(null),
    disable_scoring: yup.boolean().defined().default(false),
    timer: yup.number().min(0).defined().default(0),
    shuffle: yup.boolean().defined().default(false),
    show_zones: yup.boolean().defined().default(false),
    only_zones: yup.boolean().defined().default(false),
    disable_retry_feedback: yup.boolean().defined().default(false),
    strict: yup.boolean().defined().default(false),
    choice_start: yup.string().defined().default("A"),
    round_precision: yup.number().min(0).defined().default(0),
    nodes: CompactArray.of(yup.string().defined()).defined().ensure()
  })
  .defined()
  .when("$type", ([type]: InteractionType[], schema) => {
    const settings: Settings = {
      disable_feedback: false,
      min_correct: 1,
      max_retries: 0,
      autosubmit: true,
      autocomplete: false,
      visible_blocks: [],
      target: null,
      disable_scoring: false,
      timer: 0,
      orientation: "vertical",
      shuffle: true,
      show_zones: false,
      only_zones: false,
      disable_retry_feedback: false,
      choice_start: "",
      strict: false,
      round_precision: 0,
      nodes: []
    }

    const overrides: Record<InteractionType, Partial<Settings>> = {
      poll: { disable_feedback: true, disable_scoring: true },
      rate: { disable_feedback: true },
      "word-cloud": {
        disable_feedback: true,
        autosubmit: false,
        autocomplete: true
      },
      assessment: { autosubmit: false, shuffle: false, choice_start: "A" },
      clickzone: {},
      matching: {},
      "mini-test": { autosubmit: false, choice_start: "A" },
      "multiple-choice": { choice_start: "A" },
      "multiple-clickzone": {},
      "multiple-select": { choice_start: "A" },
      sequence: {},
      sort: {},
      "constructed-response": { autosubmit: false, autocomplete: true }
    }

    return schema.default({ ...settings, ...overrides[type] })
  }) as yup.ObjectSchema<Settings>

export const OtherSchema = yup
  .object({
    feedbacks: CompactArray.of(FeedbackSchema).defined().ensure(),
    jump: JumpSchema.nullable().default(null)
  })
  .defined() as yup.ObjectSchema<Other>

export const ExerciseStateSchema = yup
  .object({
    completed: yup.boolean().defined().default(false),
    completed_timestamp: FirestoreTimeStampSchema.defined()
      .nullable()
      .default(null)
  })
  .defined() as yup.ObjectSchema<ExerciseState>

export const QuestionSchema = yup
  .object({
    answers: CompactArray.of(AnswerSchema).defined().ensure().default([]),
    explanation: ExplanationSchema.defined().default({
      deltas: { ops: [{ insert: "\n" }] },
      videoHTMLFrame: "",
      videoThumbnailUrl: ""
    }),
    feedbacks: CompactArray.of(FeedbackSchema).defined().ensure().default([]),
    hints: CompactArray.of(HintSchema).defined().ensure().default([]),
    question: EmptyStringSchema.default(""),
    deltas: yup
      .mixed()
      .defined()
      .default({ ops: [{ insert: "\n" }] }),
    other: OtherSchema.defined().default({ feedbacks: [], jump: null }),
    settings: SettingsSchema.defined()
    // target: QuestionTargetSchema.defined().nullable().default(null)
  })
  .defined() as yup.ObjectSchema<Question>

export const SnapConfigSchema: yup.ObjectSchema<SnapConfig> = yup
  .object({
    name: EmptyStringSchema,
    group: EmptyNullableStringSchema,
    duration: yup.number().nullable().defined().default(null),
    interactionNumber: yup.number().nullable().defined().default(null),
    practiceQuestionsNumber: yup.number().nullable().defined().default(null)
  })
  .defined()

const BranchMapSchema = yup.lazy((obj: any) =>
  yup
    .object(
      _.mapValues(obj, (value, key) =>
        yup.array().of(SeqRefSchema).defined().ensure()
      )
    )
    .test("has-Main", "${path} is mising Main branch.", obj => {
      const branches = Object.keys(obj)
      return (
        branches.includes("Main") ||
        branches.includes("Level 1") ||
        branches.includes("Question Group 1")
      )
    })
    .defined()
    .default({ Main: [] })
)

const ElementStateMapSchema = yup.lazy((obj: any) =>
  yup
    .object(_.mapValues(obj, (value, key) => ElementStateSchema.defined()))
    .defined()
    .default({})
)

const ExerciseStateMapSchema = yup.lazy((obj: any) =>
  yup
    .object(_.mapValues(obj, (value, key) => ExerciseStateSchema.defined()))
    .defined()
    .default({})
)

const TopicMapSchema = yup.lazy((obj: any) =>
  yup
    .object(
      _.mapValues(obj, (value, key) =>
        yup.array().of(SnapRefSchema).defined().ensure()
      )
    )
    .test(
      "has-uncategorized",
      "${path} is mising Uncategorized topic.",
      obj => obj?.["Uncategorized"] !== undefined
    )
    .defined()
    .default({ Uncategorized: [] })
)

export const FeedbackStateSchema: yup.ObjectSchema<FeedbackState> = yup
  .object({
    exclude: CompactArray.of(yup.number().defined())
      .defined()
      .ensure()
      .default([])
  })
  .nullable()
  .defined()

export const AnswerStateSchema: yup.ObjectSchema<AnswerState> = yup
  .object({
    correct: yup.boolean().defined().default(false),
    selected: yup.boolean().defined().default(false),
    disabled: yup.boolean().defined().default(false),
    aidx: yup.number().defined().nullable().default(null),
    id: yup.string().defined().nullable().default(null),
    value: yup.string().defined().default(""),
    type: AnswerTypeSchema.defined().nullable().default(null)
  })
  .nullable()
  .defined()

export const QuestionStateSchema = yup
  .object({
    answerStates: yup.array().of(AnswerStateSchema).defined().ensure(),
    feedbackStates: yup.array().of(FeedbackStateSchema).defined().ensure(),
    generalFeedbackStates: yup
      .array()
      .of(FeedbackStateSchema)
      .defined()
      .ensure(),
    nonzoneFeedbackStates: yup
      .array()
      .of(FeedbackStateSchema)
      .defined()
      .ensure(),
    interactionType: yup.string().defined().default("multiple-choice"),
    attempts: yup.number().min(0).defined().default(0),
    completed: yup.boolean().defined().default(false),
    completed_timestamp: FirestoreTimeStampSchema.defined()
      .nullable()
      .default(null)
  })
  .defined()
  .default({
    answerStates: [],
    feedbackStates: [],
    generalFeedbackStates: [],
    nonzoneFeedbackStates: [],
    interactionType: "multiple-choice",
    attempts: 0,
    completed: false,
    completed_timestamp: null
  })

export const BlockStateSchema = yup
  .object({
    questionStates: yup
      .array()
      .of(QuestionStateSchema)
      .defined()
      .ensure()
      .when("$type", ([type]: InteractionType[], schema) => {
        return schema.default([
          {
            answerStates: [],
            feedbackStates: [],
            generalFeedbackStates: [],
            nonzoneFeedbackStates: [],
            interactionType: type,
            attempts: 0,
            completed: false,
            completed_timestamp: null
          }
        ])
      }),
    completed: yup.boolean().defined().default(false)
  })
  .nullable()
  .defined() as yup.ObjectSchema<BlockState>

export const SnapPathSchema: yup.ObjectSchema<SnapPath> = yup
  .object({
    branch: EmptyStringSchema.defined().default("Main"),
    completed: yup.boolean().defined().default(false),
    seq: yup.number().min(0).defined().default(0),
    screenStateIdx: yup.number().min(-1).defined().default(-1)
  })
  .defined()

export const ScreenStateSchema = yup
  .object({
    leftState: yup.array().of(BlockStateSchema).defined().ensure().default([]),
    rightState: yup.array().of(BlockStateSchema).defined().ensure().default([]),
    screenId: EmptyStringSchema,
    completed: yup.boolean().defined().default(false),
    completed_timestamp: FirestoreTimeStampSchema.defined()
      .nullable()
      .default(null)
  })
  .defined() as yup.ObjectSchema<ScreenState>

export const AssetSchema = yup
  .object({
    kind: yup.string().oneOf(["assets"]).defined().default("assets"),
    version: yup.number().min(1).defined().default(1),
    type: AssetTypeSchema.default("blob"),
    name: StringSchema.default("Unnamed"),
    meta: MetaTypeSchema.required(),
    altText: EmptyStringSchema,
    duration: yup.number().min(0).optional().nullable(),
    muxData: yup.object().optional().nullable()
  })
  .defined() as yup.ObjectSchema<Asset>

export const BlockEmptySchema = yup
  .object({
    kind: yup.string().oneOf(["block_empty"]).defined().default("block_empty"),
    type: yup.string().oneOf(["empty"]).defined().default("empty"),
    version: yup.number().min(1).defined().default(1),
    meta: MetaTypeSchema.required()
  })
  .defined() as yup.ObjectSchema<BlockEmpty>

export const BlockImageSchema = yup
  .object({
    kind: yup
      .string()
      .oneOf(["block_images"])
      .defined()
      .default("block_images"),
    version: yup.number().min(1).defined().default(1),
    type: yup.string().oneOf(["image"]).defined().default("image"),
    meta: MetaTypeSchema.required(),
    assets: CompactArray.of(AssetRefSchema).defined().ensure(),
    cropped: yup.boolean().defined().default(false)
    // crop: CropSchema
  })
  .defined() as yup.ObjectSchema<BlockImage>

export const BlockInteractionSchema = yup
  .object({
    kind: yup
      .string()
      .oneOf(["block_interactions"])
      .defined()
      .default("block_interactions"),
    version: yup.number().min(1).defined().default(4),
    type: InteractionTypeSchema.default("multiple-choice"),
    questions: CompactArray.of(QuestionSchema).defined().ensure(),
    meta: MetaTypeSchema.required()
  })
  .defined()
  .transform(function (v: BlockInteraction, ov) {
    if (v.version < 4) {
      const o = _.cloneDeep(v)

      if (o.version === 1) {
        o?.questions.map(question => {
          const q = question as Question & {
            require_correct: boolean
            retries: number
            autosubmit: boolean
            disable_feedback: boolean
            target: any
          }

          q.settings = {
            disable_feedback: q?.disable_feedback || false,
            min_correct: q?.require_correct ? 1 : 0,
            max_retries: q?.retries || 0,
            autosubmit: q?.autosubmit || false,
            autocomplete: false,
            visible_blocks: [],
            target: q?.target || null,
            disable_scoring: false,
            timer: 0,
            shuffle: false,
            orientation: "vertical",
            show_zones: false,
            only_zones: false,
            disable_retry_feedback: false,
            choice_start: "",
            strict: false,
            round_precision: 0,
            nodes: []
          }
        })

        o.version = 2
      }

      if (o.version === 2) {
        // Quill instantiation for html conversion...
        const node = document.createElement("div")
        const quill = new Quill(node, {})

        // convert question to deltas
        o?.questions.map((q: Question) => {
          q.deltas = quill.clipboard.convert(MDtoHTML(q.question))

          q?.feedbacks.map((f: Feedback) => {
            f.deltas = quill.clipboard.convert(MDtoHTML(f.text))
          })

          q?.answers.map((a: Answer) => {
            if (
              [
                "mini-test",
                "multiple-choice",
                "multiple-select",
                "poll",
                "assessment"
              ].includes(o.type)
            ) {
              if (a.type === "text") {
                a.deltas = quill.clipboard.convert(MDtoHTML(a.answer))
                a.type = "rich-text"
              }
            }

            a?.feedbacks.map((f: Feedback) => {
              f.deltas = quill.clipboard.convert(MDtoHTML(f.text))
            })
          })
        })

        o.version = 3
      }

      if (o.version === 3) {
        // Fix typo: videoThubnailUrl -> videoThumbnailUrl
        o?.questions.forEach((q: Question) => {
          if (q.explanation) {
            const exp = q.explanation as Explanation & {
              videoThubnailUrl?: string
            }
            q.explanation.videoThumbnailUrl = ""
            if (
              "videoThubnailUrl" in exp &&
              (exp.videoThubnailUrl || exp.videoThubnailUrl === "")
            ) {
              q.explanation.videoThumbnailUrl = exp.videoThubnailUrl
              delete exp.videoThubnailUrl
            }
          }
        })

        o.version = 4
      }

      return o
    }

    return ov
  })
  .test("is-version-4", "${path} not version 4.", obj => {
    if (obj && obj.version < 4) {
      return false
    }

    return true
  }) as yup.ObjectSchema<BlockInteraction>

export const ResponseDeclarationSchema = yup.object({
  attributes: yup.object({
    identifier: yup.string().required(),
    cardinality: yup
      .string()
      .oneOf(["order", "record", "single", "multiple"], "Invalid type")
      .default("single")
      .required()
  }),
  correctResponse: yup
    .object({
      value: CompactArray.of(yup.string()).default([])
    })
    .required(),
  areaMapping: yup.mixed(),
  mapping: yup.mixed()
}) as yup.ObjectSchema<ResponseDeclaration>

const associationAttributeSettingSchema = yup
  .object({
    maxRetries: yup.number().integer().min(0).defined().default(0).required(),
    minCorrect: yup.number().integer().min(0).defined().default(0).required(),
    minAssociations: yup.number().integer().min(0).defined().default(0),
    maxAssociations: yup.number().integer().min(1).defined().default(1),
    responseIdentifier: yup.string().defined().default(uuid()).required(),
    shuffle: yup.boolean().defined().default(false).required(),
    neutralSetting: yup.boolean().defined().default(false).required()
  })
  .required()

const minMaxAttributeSettingSchema = yup
  .object({
    maxRetries: yup.number().integer().min(0).defined().default(0).required(),
    minCorrect: yup.number().integer().min(0).defined().default(0).required(),
    minChoices: yup.number().integer().min(0).defined().default(0).required(),
    maxChoices: yup.number().integer().min(1).defined().default(1).required(),
    responseIdentifier: yup.string().defined().default(uuid()).required(),
    neutralSetting: yup.boolean().defined().default(false).required()
  })
  .required()

const simpleChoiceSettingSchema = yup
  .object({
    maxRetries: yup.number().integer().min(0).defined().default(0).required(),
    minCorrect: yup.number().integer().min(0).defined().default(0).required(),
    minChoices: yup.number().integer().min(0).defined().default(0).required(),
    maxChoices: yup.number().integer().min(1).defined().default(1).required(),
    responseIdentifier: yup.string().defined().default(uuid()).required(),
    shuffle: yup.boolean().defined().default(true).required(),
    orientation: yup.string().defined().default("vertical").required(),
    neutralSetting: yup.boolean().defined().default(false).required()
  })
  .required()

const InlineInsert = yup.object({
  // Warning: Do not default identifier with a value or it will populate all qti inlineInserts in one block_qti_interaction
  identifier: yup.string().required(),
  value: EmptyStringSchema
})

const CustomInsertObjectSchema = yup.lazy(value => {
  // If `value` is not an object (or is falsy), return a strict (without undefined, null) schema
  if (!value || typeof value !== "object")
    return yup.object().strict().noUnknown()

  // Initializes an empty object to store the schema shape dynamically
  const shape: Record<string, yup.AnySchema> = {}

  if ("hottext" in value) {
    shape.hottext = InlineInsert.required()
  }

  if ("gap" in value) {
    shape.gap = InlineInsert.required()
  }

  if ("inlineChoiceInteraction" in value) {
    shape.inlineChoiceInteraction = InlineInsert.required()
  }

  if ("textEntryInteraction" in value) {
    shape.textEntryInteraction = InlineInsert.required()
  }

  if ("image" in value) {
    shape.image = yup.string().required()
  }

  if ("formula" in value) {
    shape.formula = yup.string().required()
  }

  // If no properties were added, return a base object schema
  return yup.object().shape(shape).strict().noUnknown()
})

const GenericQtiRichText = yup
  .mixed()
  .defined()
  .default({ ops: [{ insert: "\n" }] })

const InlineContentSchema = yup.object({
  deltas: GenericQtiRichText,
  inlineChoices: yup.mixed().notRequired()
}) as yup.ObjectSchema<InlineContentType>

const simpleChoiceContentSchema = yup
  .object({
    prompt: GenericQtiRichText,
    attributes: simpleChoiceSettingSchema,
    simpleChoice: yup
      .array(
        yup.object({
          attributes: yup.object({
            identifier: yup.string().required()
          }),
          data: EmptyStringSchema,
          deltas: yup.mixed().defined().notRequired(),
          asset: AssetRefSchema.nullable().default({}),
          meta: yup.object({
            answerType: yup
              .string()
              .oneOf(["text", "math", "image", "rich-text"])
              .default("text")
              .required()
          })
        })
      )
      .min(1)
      .default([])
      .required()
  })
  .defined()
  .required()

const gapMatchContentSchema = yup
  .object({
    prompt: GenericQtiRichText,
    attributes: associationAttributeSettingSchema,
    content: InlineContentSchema,
    gapText: yup
      .array(
        yup.object({
          attributes: yup
            .object({
              identifier: StringSchema.default(uuid()),
              matchMin: yup.number().defined().default(0),
              matchMax: yup.number().defined().default(1),
              shuffle: yup.boolean().defined().default(false).required()
            })
            .notRequired(),
          data: EmptyStringSchema.notRequired(),
          deltas: yup.mixed().defined().notRequired()
        })
      )
      .defined()
      .default([])
  })
  .defined()

const hottextContentSchema = yup
  .object({
    prompt: GenericQtiRichText,
    attributes: minMaxAttributeSettingSchema,
    content: InlineContentSchema
  })
  .defined()

const inlineInteractionContentSchema = yup.object({
  prompt: GenericQtiRichText,
  attributes: yup.object({
    identifier: StringSchema.default(uuid()),
    responseIdentifier: yup.string().defined().default(uuid()).required(),
    maxRetries: yup.number().integer().min(0).defined().default(0).required(),
    minCorrect: yup.number().integer().min(0).defined().default(0).required(),
    minChoices: yup.number().integer().min(0).defined().default(0).required(),
    maxChoices: yup.number().integer().min(1).defined().default(1).required(),
    neutralSetting: yup.boolean().defined().default(false).required()
  }),
  content: InlineContentSchema
})

const matchContentSchema = yup
  .object({
    prompt: GenericQtiRichText,
    attributes: associationAttributeSettingSchema,
    simpleMatchSet: yup
      .array(
        yup
          .object({
            simpleAssociableChoice: yup
              .array(
                yup.object({
                  attributes: yup.object({
                    identifier: StringSchema.default(uuid()),
                    matchMin: yup.number().default(0),
                    matchMax: yup.number().default(0),
                    showHide: yup
                      .string()
                      .oneOf(["show", "hide"])
                      .default("show"),
                    fixed: yup.boolean().default(false)
                  }),
                  data: EmptyStringSchema,
                  deltas: yup.mixed().defined().notRequired()
                })
              )
              .defined()
          })
          .defined()
      )
      .defined()
      .default([])
  })
  .defined()

const hotspotContentSchema = yup
  .object({
    prompt: GenericQtiRichText,
    attributes: minMaxAttributeSettingSchema,
    hotspotChoice: yup
      .array(
        yup.object({
          attributes: yup
            .object({
              identifier: StringSchema.default(uuid()),
              coords: EmptyStringSchema,
              hotspotLabel: EmptyStringSchema,
              shape: ShapeTypeSchema
            })
            .notRequired()
        })
      )
      .defined()
      .default([]),
    object: yup.object({
      attributes: yup
        .object({
          data: EmptyStringSchema.required(),
          fileName: EmptyStringSchema,
          cropData: CropDataTypeScheme.default(null),
          type: AssetTypeSchema.default("image").required(),
          width: yup.number().notRequired(),
          height: yup.number().notRequired()
        })
        .notRequired()
    })
  })
  .defined()

const extendedtextContentSchema = yup.object({
  prompt: GenericQtiRichText,
  attributes: yup.object({
    responseIdentifier: yup.string().defined().default(uuid()).required(),
    neutralSetting: yup.boolean().defined().default(true).required()
  }),
  content: yup
    .object({
      content: yup
        .mixed()
        .defined()
        .default({ ops: [{ insert: "\n" }] }),
      enableRichText: yup.boolean().defined().default(true),
      characterCount: yup.string().defined().default("450"),
      limitDisplay: yup.string().oneOf(["percentage", "count"]).defined().default("count"),
      enableCrase: yup.boolean().defined().default(false),
      craseId: yup.string().defined().default("")
    })
    .defined()
})

// Individual operator (e.g., { attributes: { tex: "+" } })
const EquationOperatorSchema = yup.object({
  attributes: yup
    .object({
      tex: yup.string().required()
    })
    .required()
})

// Operator group (e.g., Basic Ops, Symbols, etc.)
const EquationOperatorGroupSchema = yup
  .object({
    attributes: yup.object({
      id: yup.string(),
      label: yup.string(),
      type: yup.string()
    }),
    equationOperator: yup.array().of(EquationOperatorSchema)
  })
  .test(
    "all-or-nothing",
    "Either provide a full operator group or leave it empty",
    value => {
      if (!value || Object.keys(value).length === 0) return true // allow empty {}

      // Enforce that both required parts are present
      return (
        typeof value.attributes === "object" &&
        Array.isArray(value.equationOperator)
      )
    }
  )
  .default({})

// A section containing operator groups (e.g., equationActionsSection)
const EquationSectionSchema = yup.object({
  equationOperators: yup.array().of(EquationOperatorGroupSchema)
})

const equationeditorContentSchema = yup.object({
  prompt: GenericQtiRichText,
  attributes: yup.object({
    id: yup.string().defined().required().default("equationEditor"),
    responseIdentifier: yup.string().defined().default(uuid()).required(),
    maxRetries: yup.number().integer().min(0).defined().default(0).required(),
    neutralSetting: yup.boolean().defined().default(false).required()
  }),
  equationActionsSection: EquationSectionSchema.required().default({}),
  equationNumbersSection: EquationSectionSchema.required().default({
    equationOperators: [
      {
        attributes: {
          id: "numbers",
          label: "Numbers",
          type: "numbers"
        },
        equationOperator: [
          {
            attributes: {
              tex: "1"
            }
          },
          {
            attributes: {
              tex: "2"
            }
          },
          {
            attributes: {
              tex: "3"
            }
          },
          {
            attributes: {
              tex: "4"
            }
          },
          {
            attributes: {
              tex: "5"
            }
          },
          {
            attributes: {
              tex: "6"
            }
          },
          {
            attributes: {
              tex: "7"
            }
          },
          {
            attributes: {
              tex: "8"
            }
          },
          {
            attributes: {
              tex: "9"
            }
          },
          {
            attributes: {
              tex: "0"
            }
          }
        ]
      },
      {
        attributes: {
          id: "numbersExtra",
          label: "Signs"
        },
        equationOperator: [
          {
            attributes: {
              tex: "."
            }
          },
          {
            attributes: {
              tex: "-"
            }
          },
          {
            attributes: {
              tex: "/"
            }
          }
        ]
      }
    ]
  })
})

export const BlockQtiInteractionSchema = yup
  .object({
    answerFeedbacks: yup.array().of(FeedbackSchema).defined().default([]),
    generalFeedbacks: CompactArray.of(FeedbackSchema)
      .defined()
      .ensure()
      .default([]),
    responseDeclaration: yup
      .array()
      .of(ResponseDeclarationSchema)
      .defined()
      .default([]),
    kind: yup
      .string()
      .oneOf(["block_qti_interactions"])
      .defined()
      .default("block_qti_interactions"),
    version: yup.number().min(1).defined().default(4),
    type: yup.string().required(),
    content: yup
      .mixed()
      .when("type", {
        is: "choice",
        then: () => simpleChoiceContentSchema
      })
      .when("type", {
        is: "order",
        then: () => simpleChoiceContentSchema
      })
      .when("type", {
        is: "gapmatch",
        then: () => gapMatchContentSchema
      })
      .when("type", {
        is: "hottext",
        then: () => hottextContentSchema
      })
      .when("type", {
        is: "match",
        then: () => matchContentSchema
      })
      .when("type", {
        is: "hotspot",
        then: () => hotspotContentSchema
      })
      .when("type", {
        is: "extendedtext",
        then: () => extendedtextContentSchema
      })
      .when("type", {
        is: "equationEditor",
        then: () => equationeditorContentSchema
      })
      .when("type", {
        is: "inlineChoice",
        then: () => inlineInteractionContentSchema
      })
      .when("type", {
        is: "textEntry",
        then: () => inlineInteractionContentSchema
      }),
    tts: TTSSchema.nullable().default(null),
    meta: MetaTypeSchema.required()
  })
  .defined()
  .transform((v: BlockQtiInteraction, ov: BlockQtiInteraction) => {
    if (v.version < 4) {
      const o = _.cloneDeep(v)
      if (o.type === "match") {
        // transform answerFeedback for match from `{rowId}_{columnId}` to `{rowId} {columnId}` to be consistent with correctResponse
        const hasUnderscore = o.answerFeedbacks.some(
          feedback =>
            feedback.answerIdentifier && feedback.answerIdentifier.includes("_")
        )
        if (hasUnderscore) {
          o.answerFeedbacks.forEach(feedback => {
            if (feedback.answerIdentifier) {
              feedback.answerIdentifier = feedback.answerIdentifier.replace(
                "_",
                " "
              )
            }
          })
        }
      }
      if (["inlineChoice", "hottext", "gapmatch"].includes(o.type)) {
        const oldDeltas = (o.content as any)?.content?.ops

        if (oldDeltas) {
          if (!(o.content as InlineInteractions).content.deltas) {
            ;(o.content as InlineInteractions).content.deltas = { ...oldDeltas }
          }

          delete (o.content as any).content.ops
        }
      }

      if (!Array.isArray(v.responseDeclaration)) {
        o.responseDeclaration = [v.responseDeclaration]
      }
      o.version = 4

      return o
    }
    if (v.version < 5) {
      const o = _.cloneDeep(v)

      if (o.type === "gapmatch") {
        o.content?.gapText.forEach(gapText => {
          gapText.deltas = gapText.deltas || {
            ops: [{ insert: gapText.data ?? "" }]
          }
        })
      } else if (o.type === "hottext") {
        o.content?.content?.deltas.ops.forEach(opsObject => {
          if (opsObject.insert?.hottext) {
            opsObject.insert.hottext.deltas = {
              ops: [{ insert: opsObject.insert.hottext.value ?? "" }]
            }
          }
        })
      } else if (o.type === "inlineChoice") {
        const inlineChoices = o.content?.content?.inlineChoices
        if (!inlineChoices) {
          o.version = 5
          return o
        }
        Object.values(inlineChoices).forEach((choiceArray: any[]) => {
          choiceArray.forEach(choice => {
            choice.deltas = choice.deltas || {
              ops: [{ insert: choice.data ?? "" }]
            }
          })
        })
      } else if (o.type === "match") {
        o.content?.simpleMatchSet.forEach(matchSet => {
          matchSet.simpleAssociableChoice.forEach(choice => {
            choice.deltas = choice.deltas || {
              ops: [{ insert: choice.data ?? "" }]
            }
          })
        })
      } else if (["order", "choice"].includes(o.type)) {
        o.content?.simpleChoice.forEach(choice => {
          if (choice.meta?.answerType === "text") {
            choice.deltas = choice.deltas || {
              ops: [{ insert: choice.data ?? "" }]
            }
            // Remove old text content from data field by resetting it to an empty string after transformation
            choice.data = ""
            choice.meta.answerType = "rich-text"
          } else {
            // For math and image (add blank newline incase it is used in future)
            choice.deltas = [{ insert: "\n" }]
          }
        })
      }
      o.version = 5
      return o
    }

    return v
  })
  .test("is-version-5", "${path} not version 5.", obj => {
    if (obj && obj.version < 5) {
      return false
    }

    return true
  }) as yup.ObjectSchema<BlockQtiInteraction>

export const BlockPdfSchema: yup.ObjectSchema<BlockPdf> = yup
  .object({
    kind: yup.string().oneOf(["block_pdfs"]).defined().default("block_pdfs"),
    version: yup.number().min(1).defined().default(1),
    type: yup.string().oneOf(["pdf"]).defined().default("pdf"),
    assets: CompactArray.of(AssetRefSchema).defined().ensure(),
    meta: MetaTypeSchema.required()
  })
  .defined() as yup.ObjectSchema<BlockPdf>

export const WordBankSchema = yup
  .object({
    kind: yup.string().oneOf(["word_banks"]).defined().default("word_banks"),
    version: yup.number().min(1).defined().default(1),
    type: yup.string().oneOf(["word-bank"]).defined().default("word-bank"),
    assets: CompactArray.of(AssetRefSchema).defined().ensure(),
    meta: MetaTypeSchema.required(),
    words: yup.array().of(yup.string().required()).required()
  })
  .defined() as yup.ObjectSchema<WordBank>

export const BlockPresentationSchema = yup
  .object({
    kind: yup
      .string()
      .oneOf(["block_presentations"])
      .defined()
      .default("block_presentations"),
    version: yup.number().min(1).defined().default(1),
    type: PresentationTypeSchema.default("fade"),
    assets: CompactArray.of(AssetRefSchema).defined().ensure(),
    autoprogress: yup.boolean().defined().default(true),
    durations: CompactArray.of(DurationSchema).defined().ensure(),
    meta: MetaTypeSchema.required()
  })
  .transform(function (v: BlockPresentation, ov: BlockPresentation) {
    // Ensure that the duration array is fully populated and the
    // start time for each slide starts at a time after the prevs
    if (v.assets?.length > 0) {
      const o = _.cloneDeep(v)

      if ((o.type as any) === "presentation") {
        o.type = "none"
      }

      let step = Math.floor(30 / o.assets.length)
      if (step < 2) step = 2
      let d: Duration = { minutes: 0, seconds: -step }

      if (o.durations === undefined) o.durations = []

      for (let i = 0; i < o.assets.length; ++i) {
        if (o.durations[i] === undefined) {
          d.seconds += step
          if (d.seconds >= 60) {
            d.seconds -= 60
            d.minutes += 1
          }

          o.durations[i] = _.clone(d)
        } else {
          const c = o.durations[i]

          if (
            c.minutes > d.minutes ||
            (c.minutes === d.minutes && c.seconds > d.seconds)
          ) {
            d = _.clone(c)
          } else {
            d.seconds += step
            if (d.seconds >= 60) {
              d.seconds -= 60
              d.minutes += 1
            }

            o.durations[i] = _.clone(d)
          }
        }
      }

      return o
    }

    return ov
  })
  .test("has-durations", "${path} missing durations.", obj => {
    if (obj?.assets && obj.assets.length === obj.durations?.length) {
      return true
    }

    return false
  })
  .test("durations-increase", "${path} is not in increasing time.", obj => {
    if (obj?.assets && obj.assets.length === obj.durations?.length) {
      let duration: Duration | undefined = undefined

      for (let i = 0; i < obj.assets.length; ++i) {
        if (obj.durations?.[i] === undefined) {
          return false
        }

        if (duration === undefined) {
          duration = _.clone(obj.durations[i])
        } else {
          if (
            obj.durations[i]?.minutes < duration?.minutes ||
            (obj.durations[i]?.minutes === duration?.minutes &&
              obj.durations[i]?.seconds <= duration?.seconds)
          ) {
            return false
          }
        }
      }
    } else {
      return false
    }

    return true
  })
  .defined() as yup.ObjectSchema<BlockPresentation>

export const BlockVideoSchema = yup
  .object({
    kind: yup
      .string()
      .oneOf(["block_videos"])
      .defined()
      .default("block_videos"),
    version: yup.number().min(1).defined().default(1),
    type: yup.string().oneOf(["video"]).defined().default("video"),
    assets: CompactArray.of(AssetRefSchema).defined().ensure(),
    transcript_id: EmptyStringSchema,
    autoPlay: yup.boolean().defined().default(true),
    showCaptions: yup.boolean().defined().default(false),
    videoTitle: EmptyStringSchema,
    meta: MetaTypeSchema.required()
  })
  .defined() as yup.ObjectSchema<BlockVideo>

export const LibrarySchema = yup
  .object({
    kind: yup.string().oneOf(["library"]).defined().default("library"),
    version: yup.number().min(1).defined().default(1),
    assets: CompactArray.of(AssetRefSchema).defined().ensure(),
    type: MediaTypeSchema.default("image"),
    name: EmptyStringSchema,
    snap_id: EmptyStringSchema,
    bucket: EmptyStringSchema,
    meta: MetaTypeSchema.required()
  })
  .defined() as yup.ObjectSchema<Media>

export const SeqConfigSchema = yup
  .object({
    kind: yup.string().oneOf(["seq_configs"]).defined().default("seq_configs"),
    version: yup.number().min(1).defined().default(1),
    name: EmptyStringSchema,
    layout: LayoutTypeSchema.default("Centered Column"),
    config: EmptyStringSchema,
    meta: MetaTypeSchema.required()
  })
  .defined() as yup.ObjectSchema<SeqConfig>

export const SeqAssessmentSchema = yup
  .object({
    kind: yup
      .string()
      .oneOf(["seq_assessments"])
      .defined()
      .default("seq_assessments"),
    version: yup.number().min(1).defined().default(1),
    name: EmptyStringSchema,
    description: EmptyStringSchema,
    layout: LayoutTypeSchema.default("Balanced"),
    mobileLayout: MobileLayoutTypeSchema.defined(),
    left: CompactArray.of(BlockRefSchema)
      .defined()
      .min(1)
      .default([{ type: "empty", id: "" }]),
    right: CompactArray.of(BlockRefSchema)
      .defined()
      .min(1)
      .default([{ type: "empty", id: "" }]),
    meta: MetaTypeSchema.required()
  })
  .defined() as yup.ObjectSchema<SeqAssessment>

export const SeqExerciseSchema = yup
  .object({
    kind: yup
      .string()
      .oneOf(["seq_exercises"])
      .defined()
      .default("seq_exercises"),
    version: yup.number().min(1).defined().default(1),
    name: EmptyStringSchema,
    layout: LayoutTypeSchema.default("Balanced"),
    mobileLayout: MobileLayoutTypeSchema.defined(),
    left: CompactArray.of(BlockRefSchema)
      .defined()
      .min(1)
      .default([{ type: "empty", id: "" }]),
    right: CompactArray.of(BlockRefSchema)
      .defined()
      .min(1)
      .default([{ type: "empty", id: "" }]),
    meta: MetaTypeSchema.required()
  })
  .defined() as yup.ObjectSchema<SeqExercise>

export const SeqLogicSchema = yup
  .object({
    kind: yup.string().oneOf(["seq_logics"]).defined().default("seq_logics"),
    version: yup.number().min(1).defined().default(1),
    name: EmptyStringSchema,
    logic: EmptyStringSchema,
    layout: LayoutTypeSchema.default("Centered Column"),
    meta: MetaTypeSchema.required()
  })
  .defined() as yup.ObjectSchema<SeqLogic>

export const SeqScreenSchema = yup
  .object({
    kind: yup.string().oneOf(["seq_screens"]).defined().default("seq_screens"),
    version: yup.number().min(1).defined().default(2),
    name: EmptyStringSchema,
    layout: LayoutTypeSchema.default("Balanced"),
    mobileLayout: MobileLayoutTypeSchema.defined(),
    left: CompactArray.of(BlockRefSchema)
      .defined()
      .min(1)
      .default([{ type: "empty", id: "" }]),
    right: CompactArray.of(BlockRefSchema)
      .defined()
      .min(1)
      .default([{ type: "empty", id: "" }]),
    meta: MetaTypeSchema.required()
  })
  .transform(function (v, ov) {
    if (v.version < 2) {
      const o = _.cloneDeep(v)
      if (!o?.mobileLayout) {
        // Ok, old array style, let's convert
        o.mobileLayout = "Stacked"
      }

      o.version = 2

      return o
    }

    return ov
  })
  .test("is-version-2", "${path} not version 2.", obj => {
    if (obj && obj.version < 2) {
      return false
    }
    return true
  })
  .defined() as yup.ObjectSchema<SeqScreen>

export const SeqQtiScreenSchema = yup
  .object({
    kind: yup
      .string()
      .oneOf(["seq_qti_screens"])
      .defined()
      .default("seq_qti_screens"),
    snapId: EmptyNullableStringSchema,
    version: yup.number().min(1).defined().default(2),
    isPublished: yup.boolean(),
    name: EmptyStringSchema,
    layout: LayoutTypeSchema.default("Balanced"),
    mobileLayout: MobileLayoutTypeSchema.defined(),
    left: CompactArray.of(BlockRefSchema)
      .defined()
      .min(1)
      .default([{ type: "empty", id: "" }]),
    right: CompactArray.of(BlockRefSchema)
      .defined()
      .min(1)
      .default([{ type: "empty", id: "" }]),
    meta: MetaTypeSchema.required(),
    exemplarQuestion: EmptyStringSchema,
    exemplarSourceTest: EmptyStringSchema,
    difficulty: yup.number().default(1),
    totalSeconds: yup.number().default(0),
    knowledgeNodeIds: CompactArray.of(yup.string()).default([]),
    standardIds: CompactArray.of(yup.string()).default([]),
    skillIds: CompactArray.of(yup.string()).default([]),
    tags: CompactArray.of(yup.string()).default([])
  })
  .transform(function (v, ov) {
    if (v.totalSeconds === null) {
      const o = _.cloneDeep(v)

      o.totalSeconds = 0

      return o
    }

    return ov
  })
  .defined() as yup.ObjectSchema<SeqQtiScreen>

export const SeqPassageSchema = yup
  .object({
    kind: yup
      .string()
      .oneOf(["seq_passages"])
      .defined()
      .default("seq_passages"),
    snapId: EmptyNullableStringSchema,
    version: yup.number().min(1).defined().default(2),
    isPublished: yup.boolean(),
    name: EmptyStringSchema,
    layout: LayoutTypeSchema.default("Centered Column"),
    mobileLayout: MobileLayoutTypeSchema.defined(),
    left: CompactArray.of(BlockRefSchema)
      .defined()
      .min(1)
      .default([{ type: "empty", id: "" }]),
    right: null,
    meta: MetaTypeSchema.required(),
    exemplarQuestion: EmptyStringSchema,
    exemplarSourceTest: EmptyStringSchema,
    difficulty: yup.number().default(1),
    totalSeconds: yup.number().default(0),
    knowledgeNodeIds: CompactArray.of(yup.string()).default([]),
    standardIds: CompactArray.of(yup.string()).default([]),
    skillIds: CompactArray.of(yup.string()).default([]),
    tags: CompactArray.of(yup.string()).default([]),
    wordCount: yup.number().default(0),
    topic: EmptyStringSchema,
    genre: EmptyStringSchema
  })
  .transform(function (v, ov) {
    if (v.totalSeconds === null) {
      const o = _.cloneDeep(v)

      o.totalSeconds = 0

      return o
    }

    return ov
  })
  .defined() as yup.ObjectSchema<SeqPassage>

export const SnapCourseSchema = yup
  .object({
    kind: yup
      .string()
      .oneOf(["snap_courses"])
      .defined()
      .default("snap_courses"),
    version: yup.number().min(1).defined().default(1),
    name: StringSchema.required(),
    published: yup.boolean().defined().default(false),
    topics: yup.lazy((obj: any) => {
      if (Array.isArray(obj)) {
        return yup
          .mixed()
          .transform(function (v, ov) {
            if (Array.isArray(v)) {
              // Ok, old array style, let's convert
              const topics: { name: string; snaps: Array<SnapRef> } = v[0]

              return { Uncategorized: topics.snaps }
            }
            return ov
          })
          .defined()
          .default({ Uncategorized: [] })
      } else {
        return yup
          .object(
            _.mapValues(obj, (value, key) =>
              yup.array().of(SnapRefSchema).defined().ensure()
            )
          )
          .test(
            "has-uncategorized",
            "${path} is missing Uncategorized topic.",
            obj => obj?.["Uncategorized"] !== undefined
          )
          .defined()
          .default({ Uncategorized: [] })
      }
    }),
    meta: MetaTypeSchema.required()
  })
  .defined() as yup.ObjectSchema<SnapCourse>

export const SnapShotSchema = yup
  .object({
    kind: yup.string().oneOf(["snap_shots"]).defined().default("snap_shots"),
    version: yup.number().min(1).defined().default(1),
    name: EmptyStringSchema,
    release: EmptyStringSchema,
    branches: BranchMapSchema,
    meta: MetaTypeSchema.required()
  })
  .defined() as yup.ObjectSchema<SnapShot>

export const SnapSchema = yup
  .object({
    kind: yup.string().oneOf(["snaps"]).defined().default("snaps"),
    version: yup.number().min(1).defined().default(2),
    name: EmptyStringSchema,
    description: EmptyNullableStringSchema,
    type: SnapTypeSchema.required().default("snap"),
    program: SnapProgramTypeSchema,
    subject: SnapSubjectTypeSchema,
    level: yup.string().optional().nullable(),
    isAllowedInContentLibrary: yup.boolean().optional().nullable(),
    adaptive: yup.boolean().defined().default(false),
    //timelines: yup.mixed().nullable(),
    published: yup.boolean().defined().default(false),
    readAloud: yup
      .object({
        lastSynthesized: FirestoreTimeStampSchema.optional()
          .nullable()
          .default(null),
        enabled: yup.boolean().defined().default(false)
      })
      .defined()
      .default({ lastSynthesized: null, enabled: false }),
    branches: BranchMapSchema,
    branchOrder: CompactArray.of(yup.string().defined()).defined().ensure(),
    globalConfig: SnapConfigSchema,
    meta: MetaTypeSchema.required().defined(),
    contentType: yup.string().optional().nullable(),
    contentId: yup.string().optional().nullable()
  })
  .transform(function (v, ov) {
    let snap

    if (!v.meta.created?.seconds) {
      snap = snap ?? _.cloneDeep(v)
      snap.meta.created = snap.meta.created
        ? firebase.firestore.Timestamp.fromDate(snap.meta.created)
        : firebase.firestore.Timestamp.now()
    }

    if (!v.meta.updated?.seconds) {
      snap = snap ?? _.cloneDeep(v)
      snap.meta.updated = snap.meta.updated
        ? firebase.firestore.Timestamp.fromDate(snap.meta.updated)
        : firebase.firestore.Timestamp.now()
    }

    if (v.version < 2) {
      const o: any = snap ?? _.cloneDeep(v)

      // is branches not defined?
      if (!v?.branches) {
        // Do we have a timeline in old format?
        if (v?.timelines && Array.isArray(v.timelines)) {
          // Ok, old array style, let's convert
          const timelines: { name: string; sequences: Array<SeqRef> } =
            o.timelines[0]
          o.branches = { Main: timelines.sequences }

          o.timelines = null
        }
      }

      o.version = 2
      return o
    }

    if (snap) {
      return snap
    }

    return ov
  })
  .test("is-version-2", "${path} not version 2.", obj => {
    if (obj && obj.version < 2) {
      return false
    }
    return true
  })
  .defined() as yup.ObjectSchema<Snap>

export const ElementStateSchema: yup.ObjectSchema<ElementState> = yup
  .object({
    exercises: ExerciseStateMapSchema
  })
  .defined()

export const ElementCourseStateSchema = yup
  .object({
    kind: yup
      .string()
      .oneOf(["element_course_state"])
      .defined()
      .default("element_course_state"),
    version: yup.number().min(1).defined().default(1),
    courseId: EmptyStringSchema,
    schoolYear: yup.number().default(() => getSchoolYear().year),
    elements: ElementStateMapSchema,
    meta: MetaTypeSchema.required()
  })
  .defined() as yup.ObjectSchema<ElementCourseState>

export const SnapStateSchema = yup
  .object({
    kind: yup.string().oneOf(["snap_state"]).defined().default("snap_state"),
    version: yup.number().min(1).defined().default(1),
    snapPaths: yup
      .array()
      .of(SnapPathSchema)
      .defined()
      .min(1)
      .default([
        { branch: "Main", seq: 0, screenStateIdx: 0, completed: false }
      ]),
    screenIdIndex: yup
      .array()
      .of(yup.string().defined())
      .defined()
      .ensure()
      .default([]),
    courseId: EmptyStringSchema,
    contentId: EmptyStringSchema,
    snapId: EmptyStringSchema,
    schoolYear: yup.number().default(() => getSchoolYear().year),
    release: yup.number().min(0).defined().default(0),
    seed: yup.number().min(0).defined().default(12345),
    completed: yup.boolean().defined().default(false),
    completed_timestamp: FirestoreTimeStampSchema.defined()
      .nullable()
      .default(null),
    screenStates: yup
      .array()
      .of(ScreenStateSchema)
      .defined()
      .min(0)
      .default([]),
    meta: MetaTypeSchema.required(),
    studentTotal: yup.object({
      totalAttempted: yup.number(),
      totalQuestions: yup.number(),
      correct: yup.number()
    })
  })
  .defined() as yup.ObjectSchema<SnapState>

export const UserSchema = yup
  .object({
    kind: yup.string().oneOf(["users"]).defined().default("users"),
    version: yup.number().min(1).defined().default(1),
    firstName: EmptyStringSchema,
    lastName: EmptyStringSchema,
    role: EmptyStringSchema,
    username: EmptyStringSchema,
    lastPasswordChange: FirestoreTimeStampSchema.nullable().default(null),
    theme: CustomThemeSchema,
    meta: MetaTypeSchema.required(),
    preferences: PreferencesSchema
  })
  .defined() as yup.ObjectSchema<User>

// Collection Schema Validators and Casters
export const collectionSchema = {
  assets: AssetSchema,
  library: LibrarySchema,
  block_interactions: BlockInteractionSchema,
  block_qti_interactions: BlockQtiInteractionSchema,
  block_presentations: BlockPresentationSchema,
  block_texts: BlockTextSchema,
  block_videos: BlockVideoSchema,
  block_images: BlockImageSchema,
  block_pdfs: BlockPdfSchema,
  block_empty: BlockEmptySchema,
  element_course_state: ElementCourseStateSchema,
  seq_assessments: SeqAssessmentSchema,
  seq_configs: SeqConfigSchema,
  seq_exercises: SeqExerciseSchema,
  seq_logics: SeqLogicSchema,
  seq_screens: SeqScreenSchema,
  seq_qti_screens: SeqQtiScreenSchema,
  seq_passages: SeqPassageSchema,
  snap_courses: SnapCourseSchema,
  snap_state: SnapStateSchema,
  snaps: SnapSchema,
  snap_shots: SnapShotSchema,
  users: UserSchema,
  word_banks: WordBankSchema
}

// Type Schema Validators and Casters
// TODO CHRIS: change the questions, assetRef to match the type name
export const typeSchema = {
  Answer: AnswerSchema,
  AnswerState: AnswerStateSchema,
  AssetRef: AssetRefSchema,
  BlockRef: BlockRefSchema,
  BlockState: BlockStateSchema,
  ElementState: ElementStateSchema,
  Feedback: FeedbackSchema,
  FeedbackState: FeedbackStateSchema,
  Hint: HintSchema,
  Jump: JumpSchema,
  Other: OtherSchema,
  Question: QuestionSchema,
  QuestionState: QuestionStateSchema,
  ScreenState: ScreenStateSchema,
  Settings: SettingsSchema,
  SnapPath: SnapPathSchema,
  SnapState: SnapStateSchema
}
